import React, {useContext, useEffect} from "react";
import MerchandisingTableContent from "domain/components/merchandising/MerchandisingTableContent";
import SelectBatchAction from "domain/components/merchandising/batchActions/SelectBatchAction";
import ListingPagination from "components/Product/Table/ListingPagination";
import ListingSortButton from "components/Product/Table/ListingSortButton";
import ListingExportButton from "components/Product/Table/ListingExportButton";
import ListingRefreshButton from "components/Product/Table/ListingRefreshButton";
import ListingTotalCount from "components/Product/Table/ListingTotalCount";
import ListingSelectTotalBanner from "components/Product/Table/ListingSelectTotalBanner";
import ItemsPerPageSelect from "components/select/itemsPerPageSelect";
import {
    Table,
    TableContainer,
    Box
} from "@mui/material";
import {FormattedMessage, useIntl} from "react-intl";
import {greyishBlue100} from "assets/jss/main";
import {ReactComponent as TaskIcon} from "assets/img/task.svg";
import {ReactComponent as CalendarIcon} from "assets/img/calendar.svg";
import {ReactComponent as CalendarTickIcon} from "assets/img/calendar-tick.svg";
import {ReactComponent as DollarCircleIcon} from "assets/img/dollar-circle.svg";
import {ReactComponent as BoxIcon} from "assets/img/box.svg";
import {ReactComponent as BoxTickIcon} from "assets/img/box-tick.svg";
import {ReactComponent as ReceiptDiscountIcon} from "assets/img/receipt-discount.svg";
import {StoreContext} from "contexts/storeContext";
import {MerchandisingContext, MerchandisingDispatchContext} from "contexts/merchandisingContext";
import {Authorizations, hasAuthorization} from "utils/authorizations";

const ITEMS_PER_PAGE_CHOICES = ["10", "25", "50"];

// same entries as the product list (ProductsTable.getSortOptions) minus the
// ranking one (the curated position order is this list's default, coming back
// to it goes through the navigation rather than the sort menu) and minus the
// scoring one (this view has no Scoring column)
const SORT_OPTIONS = [
    {value: "translations.name", labelId: "retailer.product.list.sort.name", icon: TaskIcon},
    {value: "updatedAt", labelId: "retailer.product.list.sort.updatedAt", icon: CalendarIcon},
    {value: "manualUpdatedAt", labelId: "retailer.product.list.sort.manualUpdatedAt", icon: CalendarTickIcon},
    {value: "lowestPrice", labelId: "retailer.product.list.sort.price", icon: DollarCircleIcon},
    {value: "discountRate", labelId: "retailer.product.list.sort.promotion", icon: ReceiptDiscountIcon},
    {value: "onlineSales", labelId: "retailer.product.list.sales.online", icon: BoxIcon},
    {value: "totalSales", labelId: "retailer.product.list.sales.total", icon: BoxTickIcon},
];

// same convention as the product list: fields where "the biggest first" is
// the natural reading start descending
const DESC_BY_DEFAULT = ["updatedAt", "onlineSales", "totalSales", "discountRate"];

function MerchandisingTableContainer({products, page, itemsPerPage, handlePagination, handleChangeItemsPerPage, totalItems, loading, handleExport, onRefresh, buildBody, onResetFilters, runningActions, onActionLaunched}) {
    const [selectedProducts, setSelectedProducts] = React.useState([]);
    const [globalCheck, setGlobalCheck] = React.useState(false);
    const [selectAll, setSelectAll] = React.useState(false);

    const intl = useIntl();
    const store = useContext(StoreContext);
    const navParam = useContext(MerchandisingContext);
    const dispatch = useContext(MerchandisingDispatchContext);
    const userAuthorizations = store.getState()?.authentication?.authorizations ?? [];
    const canManageProducts = hasAuthorization(userAuthorizations, Authorizations.MARKETPLACE_PRODUCT_MANAGEMENT);

    useEffect(() => {
        if (globalCheck || selectAll) {
            setSelectedProducts(products.map((p) => p.id));
        }
    }, [globalCheck, selectAll]);

    useEffect(() => {
        setSelectAll(false);
        setSelectedProducts([]);
        setGlobalCheck(false);
    }, [products])

    const handleGlobalCheckbox = (event) => {
        if (event.target.checked) {
            const selection = products.map((p) => p.id);

            setSelectedProducts(selection);
            checkGlobalCheckboxIfNeed(selection.length);
            return;
        }
        setSelectedProducts([]);
        setSelectAll(false);
        checkGlobalCheckboxIfNeed(0);
    }

    const handleSelectAll = () => {
        let selection = [];
        if (selectAll) {
            setSelectAll(false);
            setSelectedProducts(selection);
        } else {
            setSelectAll(true);
            selection = products.map((p) => p.id);

            setSelectedProducts(selection);
        }
        checkGlobalCheckboxIfNeed(selection.length);
    }

    const handleOnPageChange = (event, newPage) => {
        handlePagination(event, newPage);

        if (!selectAll) {
            checkGlobalCheckboxIfNeed(0);
        }
    }

    const checkGlobalCheckboxIfNeed = (selectionLength) => {
        if (selectionLength === products.length) {
            setGlobalCheck(true);
            return;
        }
        setGlobalCheck(false);
    }

    const handleExportClick = () => {
        handleExport(selectAll, selectedProducts);
    }

    const handleSelectSingle = (event, name) => {
        const selectedIndex = selectedProducts.indexOf(name);
        let newSelection = [];

        if (selectedIndex === -1) {
            newSelection = newSelection.concat(selectedProducts, name);
        } else if (selectedIndex === 0) {
            newSelection = newSelection.concat(selectedProducts.slice(1));
        } else if (selectedIndex === selectedProducts.length - 1) {
            newSelection = newSelection.concat(selectedProducts.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelection = newSelection.concat(
                selectedProducts.slice(0, selectedIndex),
                selectedProducts.slice(selectedIndex + 1),
            );
        }

        setSelectedProducts(newSelection);
        checkGlobalCheckboxIfNeed(newSelection.length);
    };

    // re-selecting the active option toggles asc/desc, like the product list
    const handleSortSelect = (field) => {
        const defaultDirection = DESC_BY_DEFAULT.includes(field) ? 'desc' : 'asc';
        const direction = navParam.sortField === field
            ? ('asc' === navParam.sortDirection ? 'desc' : 'asc')
            : defaultDirection;

        dispatch({
            type: 'sort',
            sortField: field,
            sortDirection: direction,
        });
    };

    const isActionsDisabled = selectedProducts.length < 1 && !selectAll;
    const showSelectTotalBanner = canManageProducts && globalCheck && products?.length > 0 && totalItems > itemsPerPage;

    return (
        <TableContainer>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px'}}>
                <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px'}}>
                    {canManageProducts && (
                        <SelectBatchAction
                            isActionsDisabled={isActionsDisabled}
                            selectedProducts={selectedProducts}
                            selectAll={selectAll}
                            totalItems={totalItems}
                            buildBody={buildBody}
                            runningActions={runningActions}
                            onActionLaunched={onActionLaunched}
                        />
                    )}
                    <ListingSortButton
                        options={SORT_OPTIONS}
                        activeValue={navParam.sortField}
                        onSelect={handleSortSelect}
                    />
                    {canManageProducts && (
                        <ListingExportButton
                            tooltip={intl.formatMessage({id: "retailer.product.list.export"})}
                            onClick={handleExportClick}
                        />
                    )}
                    <ListingRefreshButton
                        tooltip={intl.formatMessage({id: "retailer.product.list.refresh"})}
                        disabled={loading}
                        onClick={onRefresh}
                    />
                </Box>
                <ListingTotalCount
                    labelId={"retailer.product.list.label.totalProduct"}
                    total={totalItems}
                >
                    <ItemsPerPageSelect
                        choices={ITEMS_PER_PAGE_CHOICES}
                        defaultItemsPerPage={String(itemsPerPage)}
                        onChange={handleChangeItemsPerPage}
                    />
                </ListingTotalCount>
            </Box>
            {showSelectTotalBanner && (
                <ListingSelectTotalBanner
                    message={
                        <FormattedMessage
                            id={selectAll ? "retailer.product.list.totalProductSelected" : "retailer.product.list.selectedProduct"}
                            values={{nb: selectAll ? totalItems : products.length}}
                        />
                    }
                    actionLabel={
                        <FormattedMessage
                            id={selectAll ? "retailer.product.list.cancelSelect" : "retailer.product.list.selectTotal"}
                            values={{nb: totalItems}}
                        />
                    }
                    onAction={handleSelectAll}
                />
            )}
            <Box
                sx={{
                    overflowX: 'auto',
                    border: `1px solid ${greyishBlue100}`,
                    borderRadius: '8px',
                    '& td': {borderBottomColor: greyishBlue100},
                }}
            >
                <Table>
                    <MerchandisingTableContent
                        products={products}
                        selectedProducts={selectedProducts}
                        handleSelectSingle={handleSelectSingle}
                        handleGlobalCheckbox={handleGlobalCheckbox}
                        globalCheck={globalCheck}
                        loading={loading}
                        onResetFilters={onResetFilters}
                    />
                </Table>
                <ListingPagination
                    currentPage={page + 1}
                    totalPages={Math.ceil(totalItems / itemsPerPage)}
                    onPageChange={(newPage) => handleOnPageChange(null, newPage - 1)}
                />
            </Box>
        </TableContainer>
    );
}

export default MerchandisingTableContainer;
